/**
 * Phase 11 Beat 2 — Bulk Importer UI (reusable for candidate-info + evaluations).
 *
 * Flow:
 *   1. Download the Excel template.
 *   2. Fill it in and upload.
 *   3. Click "Preview & Validate" — server responds with row-level errors + counts.
 *   4. If clean, click "Commit Import" — server applies + returns a summary.
 *
 * Props:
 *   templatePath  — GET endpoint returning the .xlsx template
 *   previewPath   — POST endpoint returning validation result
 *   commitPath    — POST endpoint applying the import
 *   templateName  — default filename for template download
 *   title         — panel title
 *   description   — one-liner beneath the title
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  DownloadSimple, UploadSimple, CheckCircle, Warning, Info, ArrowsClockwise,
  FileXls, XCircle, ClockCounterClockwise,
} from "@phosphor-icons/react";
import api from "@/lib/api";

export default function BulkImporter({ templatePath, previewPath, commitPath, templateName, title, description, testidBase = "bulk" }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setSummary(null); };

  const download = async () => {
    try {
      const r = await api.get(templatePath, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = templateName; a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const doPreview = async () => {
    if (!file) { toast.error("Select a file first"); return; }
    setBusy(true); setSummary(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(previewPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      if ((r.data.errors || []).length === 0) {
        toast.success(`Preview clean — ${r.data.valid} rows ready to import.`);
      } else {
        toast.warning(`${r.data.errors.length} error${r.data.errors.length === 1 ? "" : "s"} — please fix before committing.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    if ((preview.errors || []).length > 0) {
      toast.error("Fix all row-level errors before committing.");
      return;
    }
    if (!window.confirm(`Import ${preview.valid} row${preview.valid === 1 ? "" : "s"}? This will be recorded in the Import Audit log.`)) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(commitPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(r.data.summary);
      toast.success(`Imported ${r.data.summary.imported} row${r.data.summary.imported === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const errors = preview?.errors || [];
  const counts = preview?.counts || {};

  return (
    <div className="space-y-4" data-testid={`${testidBase}-panel`}>
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
      </div>

      {/* Step 1: Template */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 text-sky-600 grid place-items-center shrink-0">
          <FileXls size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">Step 1 — Download the Excel template</div>
          <p className="text-xs text-slate-500 mt-0.5">The template includes column headers, a sample row, and a "Notes" tab with instructions.</p>
        </div>
        <button onClick={download} data-testid={`${testidBase}-download`}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-1.5 font-medium">
          <DownloadSimple size={14} /> Download template
        </button>
      </div>

      {/* Step 2: Upload */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 grid place-items-center shrink-0">
          <UploadSimple size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">Step 2 — Upload your file</div>
          <p className="text-xs text-slate-500 mt-0.5">Accepts .xlsx (recommended) or .csv. Nothing is written until you commit.</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <input type="file" accept=".xlsx,.csv"
                   onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setSummary(null); }}
                   data-testid={`${testidBase}-file`}
                   className="text-sm border border-slate-200 rounded-lg px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />
            {file && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                <FileXls size={12} /> {file.name} — {(file.size / 1024).toFixed(1)} KB
              </span>
            )}
            <button onClick={reset} disabled={!file} className="text-xs text-slate-500 hover:text-red-600 disabled:opacity-40"
                    data-testid={`${testidBase}-reset`}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Step 3: Preview */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-600 grid place-items-center shrink-0">
            <Info size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Step 3 — Preview &amp; validate</div>
            <p className="text-xs text-slate-500 mt-0.5">Runs matching (candidate_id → email → phone), duplicate detection, mandatory field validation, and dropdown checks. No data is changed.</p>
          </div>
          <button onClick={doPreview} disabled={!file || busy}
                  data-testid={`${testidBase}-preview-btn`}
                  className="text-sm bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white rounded-lg px-3 py-2 inline-flex items-center gap-1.5 font-medium shadow-sm">
            {busy ? <><ArrowsClockwise size={14} className="animate-spin" /> Running…</> : <><Info size={14} /> Preview &amp; validate</>}
          </button>
        </div>

        {preview && (
          <div className="space-y-3" data-testid={`${testidBase}-preview-result`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Metric label="Total rows" value={preview.total_rows} testid={`${testidBase}-metric-total`} />
              <Metric label="Valid" value={preview.valid} tone="emerald" testid={`${testidBase}-metric-valid`} />
              <Metric label="Errors" value={errors.length} tone={errors.length ? "red" : "slate"} testid={`${testidBase}-metric-errors`} />
              {counts.matched != null && (
                <Metric label="Matched existing" value={counts.matched} tone="indigo" testid={`${testidBase}-metric-matched`} />
              )}
              {counts.new_candidates != null && (
                <Metric label="New candidates" value={counts.new_candidates} tone="amber" testid={`${testidBase}-metric-new`} />
              )}
              {counts.will_update != null && (
                <Metric label="Will update" value={counts.will_update} tone="sky" testid={`${testidBase}-metric-update`} />
              )}
              {counts.no_change != null && (
                <Metric label="No change" value={counts.no_change} tone="slate" testid={`${testidBase}-metric-nochange`} />
              )}
            </div>

            {errors.length > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-3" data-testid={`${testidBase}-errors`}>
                <div className="text-sm font-semibold text-red-800 mb-2 inline-flex items-center gap-1.5">
                  <XCircle size={16} /> {errors.length} row-level error{errors.length === 1 ? "" : "s"} — fix these in your file and re-preview:
                </div>
                <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
                  {errors.map((e, i) => (
                    <li key={`${e.row}-${i}`} className="text-red-700 flex gap-2 border-b border-red-100 last:border-0 py-1">
                      <span className="font-mono font-semibold text-red-800 shrink-0">Row {e.row}</span>
                      <span className="text-red-600 shrink-0">·</span>
                      <span className="text-red-700 min-w-0">
                        {e.email || e.match_id || "?"}
                        <span className="ml-2 text-red-800">{(e.errors || []).join("; ")}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.preview?.length > 0 && errors.length === 0 && (
              <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
                <div className="text-sm font-medium text-emerald-800 mb-2 inline-flex items-center gap-1.5">
                  <CheckCircle size={16} /> First {Math.min(preview.preview.length, 10)} of {preview.valid} valid rows:
                </div>
                <div className="text-xs overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-200">
                        <th className="text-left py-1 pr-3">Row</th>
                        <th className="text-left py-1 pr-3">Match</th>
                        <th className="text-left py-1 pr-3">Candidate</th>
                        <th className="text-left py-1">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 10).map((p, i) => (
                        <tr key={`p-${i}`} className="border-b border-slate-100 last:border-0">
                          <td className="py-1 pr-3 font-mono">{p.row}</td>
                          <td className="py-1 pr-3">
                            {p.match_id
                              ? <span className="text-emerald-700">✓ existing</span>
                              : <span className="text-amber-700">+ new</span>}
                          </td>
                          <td className="py-1 pr-3">{p.match_name || p.email || "—"}</td>
                          <td className="py-1 text-slate-500">
                            {p.will_touch_keys && p.will_touch_keys.length > 0
                              ? `updates: ${p.will_touch_keys.slice(0, 6).join(", ")}${p.will_touch_keys.length > 6 ? "…" : ""}`
                              : (p.eval_date ? `eval ${p.eval_date}${p.will_mark_mr ? " · MR ✓" : ""}` : "no changes")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 4: Commit */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 grid place-items-center shrink-0">
          <CheckCircle size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium">Step 4 — Commit import</div>
          <p className="text-xs text-slate-500 mt-0.5">Applies the valid rows. Duplicate candidates are merged (never re-created). An entry is written to the Import Audit log.</p>
        </div>
        <button onClick={doCommit} disabled={busy || !preview || errors.length > 0 || (preview?.valid || 0) === 0}
                data-testid={`${testidBase}-commit-btn`}
                className="text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg px-3 py-2 inline-flex items-center gap-1.5 font-medium shadow-sm">
          {busy ? <><ArrowsClockwise size={14} className="animate-spin" /> Importing…</> : <><UploadSimple size={14} /> Commit import</>}
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="bg-white border border-emerald-200 rounded-xl p-4 space-y-3" data-testid={`${testidBase}-summary`}>
          <div className="text-sm font-semibold text-emerald-800 inline-flex items-center gap-1.5">
            <ClockCounterClockwise size={16} /> Import complete
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <Metric label="Total rows" value={summary.total_rows} />
            <Metric label="Imported" value={summary.imported} tone="emerald" />
            {summary.created != null && <Metric label="Created" value={summary.created} tone="sky" />}
            {summary.updated != null && <Metric label="Updated" value={summary.updated} tone="indigo" />}
            <Metric label="Failed" value={summary.failed} tone={summary.failed ? "red" : "slate"} />
            {summary.skipped != null && <Metric label="Skipped" value={summary.skipped} tone="slate" />}
          </div>
          {summary.errors && summary.errors.length > 0 && (
            <div className="text-xs text-red-700">
              <div className="font-medium mb-1">Errors during commit:</div>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {summary.errors.map((e, i) => (
                  <li key={`err-${e.row ?? "x"}-${i}`}>Row {e.row}: {(e.errors || []).join("; ")}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    red: "bg-red-50 text-red-800 border-red-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className={`rounded-lg p-3 border ${toneMap[tone] || toneMap.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}
